import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash } from "iconsax-react";
import { ModalForm, Button, Input, Toast, Prompt, PromptForm, Skeleton } from "@/shared/ui";
import { useAuth } from "@/shared/hooks";
import { extractErrorMessage } from "@/shared/utils";
import { useGetProjectQuery, useRequestProjectRemovalMutation, useCancelProjectRemovalMutation } from "../api";
import { getAvailableRemovalActions, getRemovalErrorCode } from "../utils";
import type { RemovalActionType } from "../types";

interface PromptState {
  title: string;
  text: string;
  variant?: "success" | "error" | "warning";
}

export interface RemovalCompletion {
  executed: boolean;
  actionType: RemovalActionType | null;
  kind: "requested" | "cancelled";
}

interface Props {
  projectId: number;
  onClose: () => void;
  onCompleted: (result: RemovalCompletion) => void;
}

export default function ProjectRemovalModal({ projectId, onClose, onCompleted }: Props) {
  const { t } = useTranslation("ProjectsPage");
  const { user, hasRole } = useAuth();
  const { data: detail, isLoading, isError } = useGetProjectQuery(projectId);
  const [requestRemoval, { isLoading: isSubmitting }] = useRequestProjectRemovalMutation();
  const [cancelRemoval, { isLoading: isCancelling }] = useCancelProjectRemovalMutation();

  const [reason, setReason] = useState("");
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [completion, setCompletion] = useState<RemovalCompletion | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const availableActions = detail ? getAvailableRemovalActions(detail) : [];
  const pending = detail?.pending_removal_request ?? null;
  const isChief = hasRole("chief");
  const isInitiator = !!(pending && user?.profile.full_name && pending.initiated_by_name === user.profile.full_name);
  const currentAction: RemovalActionType | null = availableActions.includes("delete")
    ? "delete"
    : (availableActions[0] ?? null);

  async function handleSubmit() {
    if (!currentAction || isSubmitting) return;
    try {
      const res = await requestRemoval({
        id: projectId,
        action_type: currentAction,
        reason: reason.trim() || undefined,
      }).unwrap();
      const executed = res.executed === true;
      if (executed && currentAction === "delete") {
        onCompleted({ executed: true, actionType: "delete", kind: "requested" });
        return;
      }
      setCompletion({ executed, actionType: currentAction, kind: "requested" });
      setPrompt({
        title: t("removal.success.title"),
        text: executed
          ? t("removal.success.executedArchive")
          : t("removal.success.requested"),
        variant: "success",
      });
    } catch (err) {
      const code = getRemovalErrorCode(err);
      setPrompt({
        title: t("removal.errorTitle"),
        text: code ? t(`removal.errors.${code}`) : extractErrorMessage(err, "removal.requestError", t),
        variant: "error",
      });
    }
  }

  async function handleCancelConfirm() {
    if (!pending || isCancelling) return;
    try {
      await cancelRemoval({ id: projectId, requestId: pending.id }).unwrap();
      setShowCancelConfirm(false);
      setCompletion({ executed: false, actionType: pending.action_type, kind: "cancelled" });
      setPrompt({ title: t("removal.success.title"), text: t("removal.success.cancelled"), variant: "success" });
    } catch (err) {
      setShowCancelConfirm(false);
      const code = getRemovalErrorCode(err);
      setPrompt({
        title: t("removal.errorTitle"),
        text: code ? t(`removal.errors.${code}`) : extractErrorMessage(err, "removal.cancelError", t),
        variant: "error",
      });
    }
  }

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess && completion) onCompleted(completion);
  }

  function renderHeader() {
    return (
      <div className="flex flex-col gap-1">
        <h4 className="text-display-2xs content-base-primary">{t("removal.modalTitle")}</h4>
        <p className="text-body-regular-sm content-base-secondary">{detail?.name}</p>
      </div>
    );
  }

  function renderBody() {
    if (isLoading || !detail) {
      return (
        <div className="flex flex-col gap-4 p-1">
          <Skeleton height={28} width={240} />
          <Skeleton height={20} width="100%" count={3} />
          <Skeleton height={80} width="100%" />
        </div>
      );
    }

    if (pending) {
      return (
        <div className="flex flex-col gap-4 p-1">
          {renderHeader()}
          <Toast
            color="notice"
            text={t(pending.action_type === "delete" ? "removal.pendingDeleteNote" : "removal.pendingArchiveNote")}
            closable={false}
            autoClose={false}
            isFullWidth
          />
          {isInitiator && (
            <Button variant="danger" size="lg" type="button" onClick={() => setShowCancelConfirm(true)} disabled={isCancelling}>
              {t("removal.cancelRequest")}
            </Button>
          )}
        </div>
      );
    }

    if (availableActions.length === 0) {
      return (
        <div className="flex flex-col gap-4 p-1">
          {renderHeader()}
          <Toast color="grey" text={t("removal.notAvailable")} closable={false} autoClose={false} isFullWidth />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-5 p-1">
        {renderHeader()}

        <Input
          isTextarea
          label={t("removal.reasonLabel")}
          placeholder={t("removal.reasonPlaceholder")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        {currentAction === "delete" && detail.is_internal && (
          <Toast color="notice" text={t("removal.internalReparentNote")} closable={false} autoClose={false} isFullWidth />
        )}

        {currentAction === "archive" && (
          <Toast color="notice" text={t("removal.archiveNote")} closable={false} autoClose={false} isFullWidth />
        )}

        <Toast
          color="grey"
          text={isChief ? t("removal.chiefImmediateNote") : t("removal.pmPendingNote")}
          closable={false}
          autoClose={false}
          isFullWidth
        />
      </div>
    );
  }

  function renderFooter() {
    if (isLoading || !detail || pending || availableActions.length === 0) return undefined;
    const isDelete = currentAction === "delete";
    return (
      <div className="grid grid-cols-[2fr_3fr] gap-3">
        <Button variant="secondary" size="huge" type="button" onClick={onClose} disabled={isSubmitting}>
          {t("removal.cancel")}
        </Button>
        <Button
          variant="destructive"
          size="huge"
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !currentAction}>
          {isSubmitting ? t("removal.submitting") : isDelete ? t("removal.submitDelete") : t("removal.submitArchive")}
        </Button>
      </div>
    );
  }

  return (
    <>
      <ModalForm icon={Trash} onClose={onClose} resize={false} footer={renderFooter()}>
        {isError && !detail ? (
          <p className="text-body-regular-md content-action-negative p-1">{t("removal.loadError")}</p>
        ) : (
          renderBody()
        )}
      </ModalForm>

      {showCancelConfirm && (
        <PromptForm
          variant="error"
          title={t("removal.cancelConfirmTitle")}
          text={t("removal.cancelConfirmText")}
          confirmText={t("removal.cancelConfirmConfirm")}
          cancelText={t("removal.cancel")}
          isLoading={isCancelling}
          onConfirm={handleCancelConfirm}
          onClose={() => setShowCancelConfirm(false)}
          namespace="ProjectsPage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={handlePromptClose}
          namespace="ProjectsPage"
        />
      )}
    </>
  );
}
